import type React from "react";
import { useMemo, useState } from "react";

import * as Sentry from "@sentry/react";
import type { AxiosInstance } from "axios";
import type {
  FieldErrors,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormSetValue,
} from "react-hook-form";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetQuotes,
  useMutationRequest,
} from "~/v2/api/hooks";
import {
  useGetCryptoApprovedWhitelistsBySymbol,
  useGetCryptoNetworkFee,
} from "~/v2/api/hooks/useGetCrypto";
import type { Position } from "~/v2/api/rest/account";
import {
  type CreateCryptoTransferPayload,
  type CryptoAsset,
  type CryptoWhitelistedAddress,
  createCryptoTransfer,
} from "~/v2/api/rest/cryptoWallet";
import {
  WITHDRAW_ACH_ERROR_MESSAGE,
  WITHDRAW_ACH_ERROR_REASON,
} from "../const";
import {
  deriveCustodian,
  deriveSendingTo,
  deriveWalletOwner,
  getUSDAmount,
} from "../util";
import {
  CREATE_TRANSFER_DEFAULTS,
  type CreateTransferFormValues,
} from "./schema";

interface UseWithdrawProps {
  accountId: string;
  isFetchingEnabled: boolean;
  setValue: UseFormSetValue<CreateTransferFormValues>;
  reset: UseFormReset<CreateTransferFormValues>;
  handleSubmit: UseFormHandleSubmit<CreateTransferFormValues>;
  errors: FieldErrors<CreateTransferFormValues>;
  chain: string;
  assetCode: string;
  address: string;
  amountStr: string;
  asset: CryptoAsset | null;
  walletSymbol: string;
  quoteSymbol: string;
  positionSymbol: string;
  positions: Position[];
  cryptoWalletAxiosInstance: AxiosInstance;
  invalidateTransfers: () => void;
  depositFromAddress: string;
}

interface WithdrawalAddressOption {
  value: string;
  label: string;
}

export interface UseWithdrawReturn {
  withdraw: {
    amount: string;
    amountError: string | null;
    usdAmount: string;
    selectedAddress: string;
    sendingTo: string | null;
    walletOwner: string | null;
    custodian: string | null;
    addressError: string | null;
    addressOptions: WithdrawalAddressOption[];
    onAmountChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onMaxAmountClick: () => void;
    isMaxDisabled: boolean;
    onAddressChange: (nextAddress: string) => void;
    onSubmit: () => void;
    onConfirm: () => void;
    onCancel: () => void;
    onEdit: () => void;
    isConfirming: boolean;
    isSubmitDisabled: boolean;
    isSubmitLoading: boolean;
    amountReceived: string;
    availableBalance: number;
    error: string | null;
    onErrorOkClicked: () => void;
    fee: string;
  };
  whitelistAddresses: string[];
  withdrawableAmount: number;
  feeAmount: number | null;
  resetWithdraw: () => void;
  cancelConfirmation: () => void;
}

export const useWithdraw = ({
  accountId,
  isFetchingEnabled,
  setValue,
  reset,
  handleSubmit,
  errors,
  chain,
  assetCode,
  address,
  amountStr,
  asset,
  walletSymbol,
  quoteSymbol,
  positionSymbol,
  positions,
  cryptoWalletAxiosInstance,
  invalidateTransfers,
  depositFromAddress,
}: UseWithdrawProps): UseWithdrawReturn => {
  const [withdrawalError, setWithdrawalError] = useState<string | null>(null);
  const [isConfirmingWithdrawal, setIsConfirmingWithdrawal] = useState(false);

  const amountNumber = useMemo(() => {
    if (amountStr.trim() === "") {
      return 0;
    }
    const numberValue = Number(amountStr);
    return Number.isNaN(numberValue) ? 0 : numberValue;
  }, [amountStr]);

  const { data: whitelistAddressesForAsset } =
    useGetCryptoApprovedWhitelistsBySymbol({
      accountId,
      symbol: walletSymbol,
      options: {
        enabled: isFetchingEnabled && !!walletSymbol,
      },
    });

  const { quotes } = useGetQuotes({
    symbols: [quoteSymbol],
    options: {
      enabled: isFetchingEnabled && !!quoteSymbol,
    },
  });

  const quote = useMemo(
    () => (quotes && quotes.length > 0 ? quotes[0] : null),
    [quotes],
  );

  const withdrawableAmount = useMemo(() => {
    const positionQtyAvailable =
      positions.find((position) => position.symbol === positionSymbol)
        ?.qty_available ?? "0";
    const positionQtyAvailableNumber = Number(positionQtyAvailable);
    return Number.isNaN(positionQtyAvailableNumber)
      ? 0
      : positionQtyAvailableNumber;
  }, [positions, positionSymbol]);

  const whitelistAddresses = useMemo(
    () =>
      (whitelistAddressesForAsset ?? [])
        .map((a) => a.address ?? "")
        .filter(Boolean),
    [whitelistAddressesForAsset],
  );

  const selectedWhitelistEntry = useMemo<CryptoWhitelistedAddress | null>(
    () =>
      (whitelistAddressesForAsset ?? []).find((w) => w.address === address) ??
      null,
    [whitelistAddressesForAsset, address],
  );

  const { sendingTo, walletOwner, custodian } = useMemo(
    () =>
      selectedWhitelistEntry
        ? {
            sendingTo: deriveSendingTo(selectedWhitelistEntry),
            walletOwner: deriveWalletOwner(selectedWhitelistEntry),
            custodian: deriveCustodian(selectedWhitelistEntry),
          }
        : { sendingTo: null, walletOwner: null, custodian: null },
    [selectedWhitelistEntry],
  );

  // Quote the network fee for the selected asset/address. When no amount has
  // been entered yet, fall back to the full withdrawable balance so a fee is
  // available to gate (and correctly compute) the Max button.
  const feeQuoteAmount = amountNumber || withdrawableAmount;

  const { data: cryptoFees } = useGetCryptoNetworkFee({
    accountId,
    params: {
      chain,
      asset: assetCode,
      amount: feeQuoteAmount,
      from_address: depositFromAddress,
      to_address: address,
    },
    options: {
      enabled:
        isFetchingEnabled &&
        !!feeQuoteAmount &&
        !!assetCode &&
        !!chain &&
        !!address &&
        !!depositFromAddress,
    },
  });

  const hasFeeQuote =
    cryptoFees?.fee !== undefined && !Number.isNaN(Number(cryptoFees.fee));

  const feeAmount =
    hasFeeQuote && !Number.isNaN(Number(cryptoFees?.fee))
      ? Number(cryptoFees?.fee)
      : null;

  const resetWithdraw = () => {
    reset(CREATE_TRANSFER_DEFAULTS);
    setWithdrawalError(null);
    setIsConfirmingWithdrawal(false);
  };

  const cancelConfirmation = () => {
    setIsConfirmingWithdrawal(false);
  };

  const withdrawMutation = useMutationRequest(
    (payload: CreateCryptoTransferPayload) =>
      createCryptoTransfer(cryptoWalletAxiosInstance, accountId, payload),
    {
      onSuccess: () => {
        toastSuccess("Withdrawal submitted — your request is being processed.");
        resetWithdraw();
        invalidateTransfers();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        if (errorMessage.includes(WITHDRAW_ACH_ERROR_REASON)) {
          setWithdrawalError(WITHDRAW_ACH_ERROR_MESSAGE);
        } else {
          toastError(`Withdrawal failed: ${errorMessage}`);
        }
      },
    },
  );

  const onWithdrawalAddressChanged = (nextAddress: string) => {
    setValue("address", nextAddress, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onAmountChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue("amount", event.target.value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onMaxAmountClick = () => {
    const totalFee = cryptoFees?.fee ? Number(cryptoFees.fee) : 0;
    const maxAfterFee = Math.max(0, withdrawableAmount - totalFee);
    setValue("amount", maxAfterFee.toString(), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onWithdrawClicked = () => {
    void handleSubmit(() => {
      setIsConfirmingWithdrawal(true);
    })();
  };

  const onConfirmWithdrawClicked = () => {
    void handleSubmit((values) => {
      withdrawMutation.mutate({
        chain: values.chain,
        asset: values.asset,
        address: values.address,
        amount: Number(values.amount),
      });
    })();
  };

  const withdrawalAddressOptions = useMemo(
    () =>
      (whitelistAddressesForAsset ?? []).map((a) => ({
        value: a.address ?? "",
        label: a.address ?? "",
      })),
    [whitelistAddressesForAsset],
  );

  const withdrawUSDAmount = useMemo(
    () => `$${getUSDAmount({ quote, amount: amountNumber })}`,
    [quote, amountNumber],
  );

  const { fee, withdrawAmountReceived } = useMemo<{
    fee: string;
    withdrawAmountReceived: string;
  }>(() => {
    if (typeof cryptoFees?.fee === "undefined") {
      return { fee: "-", withdrawAmountReceived: "-" };
    }

    const fee = Number(cryptoFees.fee);

    if (Number.isNaN(fee)) {
      Sentry.captureMessage("Crypto withdrawal fee is not a number", {
        level: "warning",
        extra: { fee: cryptoFees.fee },
      });
      return { fee: "-", withdrawAmountReceived: "-" };
    }

    const withdrawAmountReceived =
      amountNumber <= 0 ? null : amountNumber - fee;

    return {
      fee: fee >= 0 ? `${fee}` : "-",
      withdrawAmountReceived:
        withdrawAmountReceived !== null ? `${withdrawAmountReceived}` : "-",
    };
  }, [amountNumber, cryptoFees]);

  const isWithdrawDisabled =
    !isFetchingEnabled ||
    !asset ||
    !address ||
    !amountNumber ||
    withdrawMutation.isPending;

  return {
    withdraw: {
      amount: amountStr,
      amountError: errors.amount?.message ?? null,
      usdAmount: withdrawUSDAmount,
      selectedAddress: address,
      sendingTo,
      walletOwner,
      custodian,
      addressError: errors.address?.message ?? null,
      addressOptions: withdrawalAddressOptions,
      onAmountChange: onAmountChanged,
      onMaxAmountClick,
      isMaxDisabled: withdrawableAmount <= 0 || !hasFeeQuote,
      onAddressChange: onWithdrawalAddressChanged,
      onSubmit: onWithdrawClicked,
      onConfirm: onConfirmWithdrawClicked,
      onCancel: resetWithdraw,
      onEdit: cancelConfirmation,
      isConfirming: isConfirmingWithdrawal,
      isSubmitDisabled: isWithdrawDisabled,
      isSubmitLoading: withdrawMutation.isPending,
      amountReceived: withdrawAmountReceived,
      availableBalance: withdrawableAmount,
      error: withdrawalError,
      onErrorOkClicked: resetWithdraw,
      fee,
    },
    whitelistAddresses,
    withdrawableAmount,
    feeAmount,
    resetWithdraw,
    cancelConfirmation,
  };
};
